import { deriveVaultWrappingKey } from './vaultKeyDerivation';
import {
  bytesToBase64,
  generateVaultIv,
  generateVaultSalt,
  getVaultCryptoMetadata,
} from './vaultCryptoUtils';
import {
  SENSITIVE_VAULT_AES_ALGO,
  SENSITIVE_VAULT_VERSION,
} from './vaultCryptoConfig';

const WRAP_IV_LENGTH = 12;
const DEFAULT_WRAP_ALGO = SENSITIVE_VAULT_AES_ALGO;
const DEFAULT_VAULT_ALGO = SENSITIVE_VAULT_AES_ALGO;
const DEFAULT_VAULT_VERSION = SENSITIVE_VAULT_VERSION;

function getStoredKdfAlgorithm(kdf) {
  const normalized = String(kdf || '').toLowerCase();

  if (normalized === 'pbkdf2') {
    return 'pbkdf2-sha256';
  }

  return normalized;
}

function getWebCrypto() {
  const cryptoObj = globalThis?.crypto;
  if (!cryptoObj?.subtle || typeof cryptoObj.getRandomValues !== 'function') {
    throw new Error('Web Crypto API is not available');
  }
  return cryptoObj;
}

function randomBytes(length) {
  const cryptoObj = getWebCrypto();
  const bytes = new Uint8Array(length);
  cryptoObj.getRandomValues(bytes);
  return bytes;
}

function randomRecoveryChunk(length = 4) {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = randomBytes(length);
  let out = '';

  for (let i = 0; i < length; i += 1) {
    out += alphabet[bytes[i] % alphabet.length];
  }

  return out;
}

export function generateRecoveryKey() {
  const chunks = Array.from({ length: 7 }, () => randomRecoveryChunk(4));
  return `MSDB-RCVR-${chunks.join('-')}`;
}

export async function generateVaultKey() {
  const cryptoObj = getWebCrypto();

  return cryptoObj.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, [
    'encrypt',
    'decrypt',
  ]);
}

async function importWrappingKeyFromBytes(rawKeyBytes) {
  const cryptoObj = getWebCrypto();

  return cryptoObj.subtle.importKey(
    'raw',
    rawKeyBytes,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

export async function wrapVaultKeyWithKek({ vaultKey, kek }) {
  const cryptoObj = getWebCrypto();

  const rawVaultKey = await cryptoObj.subtle.exportKey('raw', vaultKey);
  const iv = generateVaultIv();

  if (iv.length !== WRAP_IV_LENGTH) {
    throw new Error(`Vault wrap IV must be ${WRAP_IV_LENGTH} bytes`);
  }

  const encrypted = await cryptoObj.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv,
    },
    kek,
    rawVaultKey
  );

  const encryptedBytes = new Uint8Array(encrypted);
  const combined = new Uint8Array(iv.length + encryptedBytes.length);
  combined.set(iv, 0);
  combined.set(encryptedBytes, iv.length);

  return bytesToBase64(combined);
}


export async function buildVaultKeyWrapper({
  vaultKey,
  secret,
  wrapperType,
  wrapperLabel,
  keyVersion = 1,
}) {
  if (!vaultKey) {
    throw new Error('Unlocked vault key is required');
  }

  if (!secret || !String(secret).trim()) {
    throw new Error('A passphrase or recovery key is required');
  }

  if (!['passphrase', 'recovery_key'].includes(wrapperType)) {
    throw new Error('Unsupported vault wrapper type');
  }

  const metadata = getVaultCryptoMetadata();
  const saltBytes = generateVaultSalt();
  const salt = bytesToBase64(saltBytes);

  const kekBytes = await deriveVaultWrappingKey({
    passphrase: secret,
    salt: saltBytes,
    iterations: metadata.iterations,
    kdf: metadata.kdf,
  });

  const kek = await importWrappingKeyFromBytes(kekBytes);
  const wrappedVaultKey = await wrapVaultKeyWithKek({ vaultKey, kek });

  return {
    wrapperType,
    wrapperLabel,
    wrappedVaultKey,
    wrapAlgo: DEFAULT_WRAP_ALGO,
    kdfAlgo: getStoredKdfAlgorithm(metadata.kdf),
    kdfSalt: salt,
    kdfParams: {
      iterations: metadata.iterations,
    },
    keyVersion,
  };
}

export async function buildPassphraseWrapper({ vaultKey, passphrase, keyVersion = 1 }) {
  return buildVaultKeyWrapper({
    vaultKey,
    secret: passphrase,
    wrapperType: 'passphrase',
    wrapperLabel: 'Primary passphrase',
    keyVersion,
  });
}

export async function buildRecoveryKeyWrapper({ vaultKey, keyVersion = 1 }) {
  const recoveryKey = generateRecoveryKey();
  const wrapper = await buildVaultKeyWrapper({
    vaultKey,
    secret: recoveryKey,
    wrapperType: 'recovery_key',
    wrapperLabel: 'Recovery key',
    keyVersion,
  });

  return { recoveryKey, wrapper };
}

export async function buildVaultSetupPayload({ passphrase }) {
  if (!passphrase || !String(passphrase).trim()) {
    throw new Error('Vault passphrase is required');
  }

  const metadata = getVaultCryptoMetadata();
  const vaultKey = await generateVaultKey();

  // Passphrase wrapper
  const passphraseSaltBytes = generateVaultSalt();
  const passphraseSalt = bytesToBase64(passphraseSaltBytes);

  const passphraseKekBytes = await deriveVaultWrappingKey({
    passphrase,
    salt: passphraseSaltBytes,
    iterations: metadata.iterations,
    kdf: metadata.kdf,
  });

  const passphraseKek = await importWrappingKeyFromBytes(passphraseKekBytes);

  const wrappedVaultKeyForPassphrase = await wrapVaultKeyWithKek({
    vaultKey,
    kek: passphraseKek,
  });

  // Recovery key wrapper
  const recoveryKey = generateRecoveryKey();
  const recoverySaltBytes = generateVaultSalt();
  const recoverySalt = bytesToBase64(recoverySaltBytes);

  const recoveryKekBytes = await deriveVaultWrappingKey({
    passphrase: recoveryKey,
    salt: recoverySaltBytes,
    iterations: metadata.iterations,
    kdf: metadata.kdf,
  });

  const recoveryKek = await importWrappingKeyFromBytes(recoveryKekBytes);

  const wrappedVaultKeyForRecovery = await wrapVaultKeyWithKek({
    vaultKey,
    kek: recoveryKek,
  });

  return {
    vaultKey,
    recoveryKey,
    payload: {
      vault: {
        vaultVersion: DEFAULT_VAULT_VERSION,
        vaultAlgo: DEFAULT_VAULT_ALGO,
      },
      wrappers: [
        {
          wrapperType: 'passphrase',
          wrapperLabel: 'Primary passphrase',
          wrappedVaultKey: wrappedVaultKeyForPassphrase,
          wrapAlgo: DEFAULT_WRAP_ALGO,
          kdfAlgo: getStoredKdfAlgorithm(metadata.kdf),
          kdfSalt: passphraseSalt,
          kdfParams: {
            iterations: metadata.iterations,
          },
          keyVersion: 1,
        },
        {
          wrapperType: 'recovery_key',
          wrapperLabel: 'Recovery key',
          wrappedVaultKey: wrappedVaultKeyForRecovery,
          wrapAlgo: DEFAULT_WRAP_ALGO,
          kdfAlgo: getStoredKdfAlgorithm(metadata.kdf),
          kdfSalt: recoverySalt,
          kdfParams: {
            iterations: metadata.iterations,
          },
          keyVersion: 1,
        },
      ],
    },
  };
}
